// Web Worker for bot calculations to prevent UI blocking
import { GameState, getAllLegalMoves, validateMove } from './chess-engine';
import { getOpeningMove, getFenFromGameState } from './opening-book';

export interface WorkerMessage {
  type: 'CALCULATE_MOVE';
  gameState: GameState;
  botElo: number;
  timeLimit: number;
  analyze?: boolean;
}

export interface WorkerResponse {
  type: 'MOVE_CALCULATED';
  move: any;
  evaluation: number;
  depth: number;
  nodesSearched: number;
}

// This would be the content of the Web Worker
export const botWorkerCode = `
// Import necessary functions (in a real implementation, these would be bundled)
${getAllLegalMoves.toString()}
${validateMove.toString()}
${getOpeningMove.toString()}
${getFenFromGameState.toString()}

// Bot personalities
const botPersonalities = {
  200: {
    name: "Ella",
    elo: 200,
    blunderRate: 0.4,
    tacticalStrength: 0.1,
    positionalUnderstanding: 0.05,
    endgameKnowledge: 0.1,
    openingKnowledge: 0.1,
    maxDepth: 2
  },
  400: {
    name: "Marty",
    elo: 400,
    blunderRate: 0.3,
    tacticalStrength: 0.2,
    positionalUnderstanding: 0.15,
    endgameKnowledge: 0.15,
    openingKnowledge: 0.2,
    maxDepth: 2
  },
  800: {
    name: "Carlos",
    elo: 800,
    blunderRate: 0.2,
    tacticalStrength: 0.3,
    positionalUnderstanding: 0.2,
    endgameKnowledge: 0.2,
    openingKnowledge: 0.3,
    maxDepth: 3
  },
  1400: {
    name: "Jason",
    elo: 1400,
    blunderRate: 0.08,
    tacticalStrength: 0.7,
    positionalUnderstanding: 0.5,
    endgameKnowledge: 0.6,
    openingKnowledge: 0.7,
    maxDepth: 4
  },
  3200: {
    name: "Magnus",
    elo: 3200,
    blunderRate: 0.005,
    tacticalStrength: 0.98,
    positionalUnderstanding: 0.95,
    endgameKnowledge: 0.98,
    openingKnowledge: 0.95,
    maxDepth: 6
  }
};

// Piece values
const PIECE_VALUES = {
  'p': 100, 'n': 300, 'b': 320, 'r': 500, 'q': 900, 'k': 10000,
  'P': 100, 'N': 300, 'B': 320, 'R': 500, 'Q': 900, 'K': 10000
};

// Piece-square tables
const PAWN_TABLE = [
  [0,  0,  0,  0,  0,  0,  0,  0],
  [50, 50, 50, 50, 50, 50, 50, 50],
  [10, 10, 20, 30, 30, 20, 10, 10],
  [5,  5, 10, 25, 25, 10,  5,  5],
  [0,  0,  0, 20, 20,  0,  0,  0],
  [5, -5,-10,  0,  0,-10, -5,  5],
  [5, 10, 10,-20,-20, 10, 10,  5],
  [0,  0,  0,  0,  0,  0,  0,  0]
];

const KNIGHT_TABLE = [
  [-50,-40,-30,-30,-30,-30,-40,-50],
  [-40,-20,  0,  0,  0,  0,-20,-40],
  [-30,  0, 10, 15, 15, 10,  0,-30],
  [-30,  5, 15, 20, 20, 15,  5,-30],
  [-30,  0, 15, 20, 20, 15,  0,-30],
  [-30,  5, 10, 15, 15, 10,  5,-30],
  [-40,-20,  0,  5,  5,  0,-20,-40],
  [-50,-40,-30,-30,-30,-30,-40,-50]
];

const BISHOP_TABLE = [
  [-20,-10,-10,-10,-10,-10,-10,-20],
  [-10,  0,  0,  0,  0,  0,  0,-10],
  [-10,  0,  5, 10, 10,  5,  0,-10],
  [-10,  5,  5, 10, 10,  5,  5,-10],
  [-10,  0, 10, 10, 10, 10,  0,-10],
  [-10, 10, 10, 10, 10, 10, 10,-10],
  [-10,  5,  0,  0,  0,  0,  5,-10],
  [-20,-10,-10,-10,-10,-10,-10,-20]
];

const ROOK_TABLE = [
  [0,  0,  0,  0,  0,  0,  0,  0],
  [5, 10, 10, 10, 10, 10, 10,  5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [0,  0,  0,  5,  5,  0,  0,  0]
];

const QUEEN_TABLE = [
  [-20,-10,-10, -5, -5,-10,-10,-20],
  [-10,  0,  0,  0,  0,  0,  0,-10],
  [-10,  0,  5,  5,  5,  5,  0,-10],
  [-5,  0,  5,  5,  5,  5,  0, -5],
  [0,  0,  5,  5,  5,  5,  0, -5],
  [-10,  5,  5,  5,  5,  5,  0,-10],
  [-10,  0,  5,  0,  0,  0,  0,-10],
  [-20,-10,-10, -5, -5,-10,-10,-20]
];

const KING_TABLE = [
  [-30,-40,-40,-50,-50,-40,-40,-30],
  [-30,-40,-40,-50,-50,-40,-40,-30],
  [-30,-40,-40,-50,-50,-40,-40,-30],
  [-30,-40,-40,-50,-50,-40,-40,-30],
  [-20,-30,-30,-40,-40,-30,-30,-20],
  [-10,-20,-20,-20,-20,-20,-20,-10],
  [20, 20,  0,  0,  0,  0, 20, 20],
  [20, 30, 10,  0,  0, 10, 30, 20]
];

function evaluatePosition(gameState, personality) {
  let score = 0;
  let whiteKingPos = null;
  let blackKingPos = null;
  
  // Material and positional evaluation
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = gameState.board[row][col];
      if (piece) {
        const value = PIECE_VALUES[piece.type.toLowerCase()];
        const multiplier = piece.color === 'white' ? 1 : -1;
        score += value * multiplier;
        
        // Track king positions
        if (piece.type.toLowerCase() === 'k') {
          if (piece.color === 'white') {
            whiteKingPos = [row, col];
          } else {
            blackKingPos = [row, col];
          }
        }
        
        // Positional bonuses based on personality
        if (personality.positionalUnderstanding > 0.2) {
          let tableValue = 0;
          const pieceType = piece.type.toLowerCase();
          
          if (pieceType === 'p') {
            tableValue = piece.color === 'white' ? PAWN_TABLE[row][col] : PAWN_TABLE[7-row][col];
          } else if (pieceType === 'n') {
            tableValue = piece.color === 'white' ? KNIGHT_TABLE[row][col] : KNIGHT_TABLE[7-row][col];
          } else if (pieceType === 'b') {
            tableValue = piece.color === 'white' ? BISHOP_TABLE[row][col] : BISHOP_TABLE[7-row][col];
          } else if (pieceType === 'r') {
            tableValue = piece.color === 'white' ? ROOK_TABLE[row][col] : ROOK_TABLE[7-row][col];
          } else if (pieceType === 'q') {
            tableValue = piece.color === 'white' ? QUEEN_TABLE[row][col] : QUEEN_TABLE[7-row][col];
          } else if (pieceType === 'k') {
            tableValue = piece.color === 'white' ? KING_TABLE[row][col] : KING_TABLE[7-row][col];
          }
          
          score += tableValue * multiplier * personality.positionalUnderstanding;
        }
      }
    }
  }
  
  // King safety evaluation for higher-level bots
  if (personality.positionalUnderstanding > 0.5 && whiteKingPos && blackKingPos) {
    // Penalty for exposed kings
    const whiteKingSafety = evaluateKingSafety(gameState, whiteKingPos, 'white');
    const blackKingSafety = evaluateKingSafety(gameState, blackKingPos, 'black');
    score += (whiteKingSafety - blackKingSafety) * personality.positionalUnderstanding * 10;
  }
  
  // Adjust score based on side to move
  if (gameState.sideToMove === 'black') {
    score = -score;
  }
  
  return score;
}

function evaluateKingSafety(gameState, kingPos, color) {
  let safety = 0;
  const [kingRow, kingCol] = kingPos;
  
  // Check pawn shield
  if (color === 'white' && kingRow >= 6) {
    // Check pawns in front of king
    for (let col = Math.max(0, kingCol - 1); col <= Math.min(7, kingCol + 1); col++) {
      if (kingRow - 1 >= 0) {
        const piece = gameState.board[kingRow - 1][col];
        if (piece && piece.type.toLowerCase() === 'p' && piece.color === 'white') {
          safety += 15;
        }
      }
    }
  } else if (color === 'black' && kingRow <= 1) {
    // Check pawns in front of king
    for (let col = Math.max(0, kingCol - 1); col <= Math.min(7, kingCol + 1); col++) {
      if (kingRow + 1 <= 7) {
        const piece = gameState.board[kingRow + 1][col];
        if (piece && piece.type.toLowerCase() === 'p' && piece.color === 'black') {
          safety += 15;
        }
      }
    }
  }
  
  return safety;
}

// Alpha-beta search implementation
function alphaBeta(gameState, depth, alpha, beta, maximizingPlayer, personality, timeLimit, startTime, stats) {
  if (Date.now() - startTime > timeLimit) {
    throw new Error('Time limit exceeded');
  }
  
  stats.nodesSearched++;
  
  if (depth === 0) {
    return evaluatePosition(gameState, personality);
  }
  
  const moves = getAllLegalMoves(gameState);
  
  if (moves.length === 0) {
    // Check if it's checkmate or stalemate
    return maximizingPlayer ? -10000 : 10000;
  }
  
  if (maximizingPlayer) {
    let maxEval = -Infinity;
    for (const move of moves) {
      const moveResult = validateMove(gameState, move.from, move.to, move.promotion);
      if (moveResult.isLegal && moveResult.newState) {
        const eval = alphaBeta(moveResult.newState, depth - 1, alpha, beta, false, personality, timeLimit, startTime, stats);
        maxEval = Math.max(maxEval, eval);
        alpha = Math.max(alpha, eval);
        if (beta <= alpha) {
          break; // Beta cutoff
        }
      }
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const move of moves) {
      const moveResult = validateMove(gameState, move.from, move.to, move.promotion);
      if (moveResult.isLegal && moveResult.newState) {
        const eval = alphaBeta(moveResult.newState, depth - 1, alpha, beta, true, personality, timeLimit, startTime, stats);
        minEval = Math.min(minEval, eval);
        beta = Math.min(beta, eval);
        if (beta <= alpha) {
          break; // Alpha cutoff
        }
      }
    }
    return minEval;
  }
}

function findBestMove(gameState, personality, timeLimit) {
  const startTime = Date.now();
  const moves = getAllLegalMoves(gameState);
  
  if (moves.length === 0) {
    return { move: null, evaluation: 0, depth: 0, nodesSearched: 0 };
  }
  
  // Check opening book first for higher-level bots
  if (personality.openingKnowledge > 0.5) {
    const fen = getFenFromGameState(gameState);
    const openingMove = getOpeningMove(fen, personality.openingKnowledge);
    if (openingMove) {
      return { 
        move: openingMove, 
        evaluation: 0, 
        depth: 0, 
        nodesSearched: 1,
        isOpeningMove: true 
      };
    }
  }
  
  let bestMove = moves[0];
  let bestEval = -Infinity;
  let searchDepth = 0;
  let totalNodesSearched = 0;
  
  // Iterative deepening
  try {
    for (let depth = 1; depth <= personality.maxDepth; depth++) {
      let currentBestMove = null;
      let currentBestEval = -Infinity;
      const stats = { nodesSearched: 0 };
      
      for (const move of moves) {
        const moveResult = validateMove(gameState, move.from, move.to, move.promotion);
        if (moveResult.isLegal && moveResult.newState) {
          try {
            const eval = -alphaBeta(
              moveResult.newState, 
              depth - 1, 
              -Infinity, 
              Infinity, 
              false, 
              personality, 
              timeLimit, 
              startTime, 
              stats
            );
            
            if (eval > currentBestEval) {
              currentBestEval = eval;
              currentBestMove = move;
            }
          } catch (timeError) {
            break; // Time limit exceeded, use best move from previous iteration
          }
        }
      }
      
      if (currentBestMove) {
        bestMove = currentBestMove;
        bestEval = currentBestEval;
        searchDepth = depth;
        totalNodesSearched += stats.nodesSearched;
      }
      
      // If we're running out of time, break
      if (Date.now() - startTime > timeLimit * 0.8) {
        break;
      }
    }
  } catch (error) {
    // Time limit exceeded, use best move found so far
  }
  
  // Apply blunder probability
  if (Math.random() < personality.blunderRate) {
    // Pick a worse move
    const randomIndex = Math.floor(Math.random() * Math.min(moves.length, 5));
    bestMove = moves[randomIndex];
    bestEval -= 200; // Penalty for blunder
  }
  
  return { 
    move: bestMove, 
    evaluation: bestEval, 
    depth: searchDepth, 
    nodesSearched: totalNodesSearched 
  };
}

// Worker message handler
self.addEventListener('message', function(e) {
  const { type, gameState, botElo, timeLimit } = e.data;
  
  if (type === 'CALCULATE_MOVE') {
    const personality = botPersonalities[botElo];
    if (!personality) {
      // Fallback for unknown ELO
      const moves = getAllLegalMoves(gameState);
      const randomMove = moves[Math.floor(Math.random() * moves.length)];
      self.postMessage({
        type: 'MOVE_CALCULATED',
        move: randomMove,
        evaluation: 0,
        depth: 0,
        nodesSearched: 1
      });
      return;
    }
    
    const result = findBestMove(gameState, personality, timeLimit);
    
    self.postMessage({
      type: 'MOVE_CALCULATED',
      move: result.move,
      evaluation: result.evaluation,
      depth: result.depth,
      nodesSearched: result.nodesSearched
    });
  }
});
`;

export function createBotWorker(): Worker {
  // Use the proper module worker
  return new Worker(new URL('../workers/botWorker.ts', import.meta.url), { type: 'module' });
}
